import React, { createContext, useState, useContext, useEffect } from 'react';
import { useAuth } from './AuthContext';

interface Contract {
  id: string;
  name: string;
  price: number;
  category: string;
}

interface CartItem {
  contract: Contract;
  quantity: number;
}

interface CartContextType {
  items: CartItem[];
  addItem: (contract: Contract) => void;
  removeItem: (contractId: string) => void;
  updateQuantity: (contractId: string, quantity: number) => void;
  clearCart: () => void;
  totalItems: number;
  totalPrice: number;
}

const CartContext = createContext<CartContextType | undefined>(undefined);

export const useCart = () => {
  const context = useContext(CartContext);
  if (context === undefined) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [items, setItems] = useState<CartItem[]>([]);
  const { user } = useAuth();
  
  // Load cart from localStorage on initial render
  useEffect(() => {
    const savedCart = localStorage.getItem('cart');
    if (savedCart) {
      setItems(JSON.parse(savedCart));
    }
  }, []);
  
  // Save cart to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(items));
  }, [items]);
  
  const addItem = (contract: Contract) => {
    setItems(prevItems => {
      const existingItem = prevItems.find(item => item.contract.id === contract.id);
      
      if (existingItem) {
        return prevItems.map(item => 
          item.contract.id === contract.id 
            ? { ...item, quantity: item.quantity + 1 } 
            : item
        );
      } else {
        return [...prevItems, { contract, quantity: 1 }];
      }
    });
  };
  
  const removeItem = (contractId: string) => {
    setItems(prevItems => prevItems.filter(item => item.contract.id !== contractId));
  };
  
  const updateQuantity = (contractId: string, quantity: number) => {
    if (quantity <= 0) {
      removeItem(contractId);
      return;
    }
    
    setItems(prevItems => 
      prevItems.map(item => 
        item.contract.id === contractId 
          ? { ...item, quantity } 
          : item
      )
    );
  };
  
  const clearCart = () => {
    setItems([]);
  };
  
  const totalItems = items.reduce((total, item) => total + item.quantity, 0);
  
  const totalPrice = items.reduce(
    (total, item) => total + (item.contract.price * item.quantity), 
    0
  );
  
  return (
    <CartContext.Provider value={{ 
      items, 
      addItem, 
      removeItem, 
      updateQuantity, 
      clearCart, 
      totalItems, 
      totalPrice 
    }}>
      {children}
    </CartContext.Provider>
  );
};